import { supabase } from "@/integrations/supabase/client";

export interface CampaignPlan {
  id: string;
  company_id: string;
  name: string;
  goal: string;
  idea_prompt: string;
  strategic_brief: any;
  tone: string;
  target_audience: string | null;
  status: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignPlanAsset {
  id: string;
  campaign_plan_id: string;
  asset_type: string;
  title: string;
  content: string;
  metadata: any;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const fetchCampaignPlans = async (companyId: string): Promise<CampaignPlan[]> => {
  const { data, error } = await supabase
    .from('campaign_plans' as any)
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as CampaignPlan[];
};

export const fetchCampaignPlan = async (id: string): Promise<CampaignPlan> => {
  const { data, error } = await supabase
    .from('campaign_plans' as any)
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as unknown as CampaignPlan;
};

export const createCampaignPlan = async (plan: Partial<CampaignPlan>): Promise<CampaignPlan> => {
  const { data, error } = await supabase
    .from('campaign_plans' as any)
    .insert(plan as any)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as CampaignPlan;
};

export const updateCampaignPlan = async (id: string, updates: Partial<CampaignPlan>): Promise<CampaignPlan> => {
  const { data, error } = await supabase
    .from('campaign_plans' as any)
    .update({ ...updates, updated_at: new Date().toISOString() } as any)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as CampaignPlan;
};

export const deleteCampaignPlan = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('campaign_plans' as any)
    .delete()
    .eq('id', id);
  if (error) throw error;
};

export const fetchCampaignPlanAssets = async (planId: string): Promise<CampaignPlanAsset[]> => {
  const { data, error } = await supabase
    .from('campaign_plan_assets' as any)
    .select('*')
    .eq('campaign_plan_id', planId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as CampaignPlanAsset[];
};

export const upsertCampaignPlanAssets = async (assets: Partial<CampaignPlanAsset>[]): Promise<CampaignPlanAsset[]> => {
  const { data, error } = await supabase
    .from('campaign_plan_assets' as any)
    .upsert(assets as any)
    .select();
  if (error) throw error;
  return (data || []) as unknown as CampaignPlanAsset[];
};

export const updateCampaignPlanAsset = async (id: string, updates: Partial<CampaignPlanAsset>): Promise<CampaignPlanAsset> => {
  const { data, error } = await supabase
    .from('campaign_plan_assets' as any)
    .update({ ...updates, updated_at: new Date().toISOString() } as any)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as CampaignPlanAsset;
};
